import { create } from 'zustand';

import { TranscriptInconsistency, TranscriptInconsistencyStatus } from '@/api/types/backend-dto-types';

interface TranscriptInconsistenciesState {
  // Store inconsistencies by transcriptId - each transcript has its own array of inconsistencies
  inconsistencies: Record<string, TranscriptInconsistency[]>;

  // Initialize inconsistencies for a transcript (from saved data)
  initializeInconsistencies: (transcriptId: string, inconsistencies: TranscriptInconsistency[]) => void;

  // Add a new inconsistency (from WebSocket events)
  addInconsistency: (transcriptId: string, inconsistency: TranscriptInconsistency) => void;

  // Update an inconsistency (for status changes, etc.)
  updateInconsistency: (
    transcriptId: string,
    inconsistencyId: string,
    updates: Partial<TranscriptInconsistency>,
  ) => void;

  // Update inconsistency status specifically
  updateInconsistencyStatus: (
    transcriptId: string,
    inconsistencyId: string,
    status: TranscriptInconsistencyStatus,
  ) => void;

  // Mark all inconsistencies that reference a specific block as reprocessing
  markBlockInconsistenciesAsReprocessing: (transcriptId: string, blockId: string) => void;

  // Mark all inconsistencies that reference a specific block as invalidated (backwards compatibility)
  markBlockInconsistenciesAsInvalidated: (transcriptId: string, blockId: string) => void;

  // Get inconsistencies for a transcript with optional status filtering
  getInconsistencies: (
    transcriptId: string,
    includeInvalidated?: boolean,
    includeDismissed?: boolean,
  ) => TranscriptInconsistency[];

  // Clear inconsistencies for a transcript
  clearInconsistencies: (transcriptId: string) => void;

  // Clear all inconsistencies
  clearAllInconsistencies: () => void;

  // Convert reprocessing inconsistencies to invalidated when pipeline completes
  convertReprocessingToInvalidated: (transcriptId: string, blockId?: string) => void;
}

export const useTranscriptInconsistenciesStore = create<TranscriptInconsistenciesState>((set, get) => ({
  inconsistencies: {},

  initializeInconsistencies: (transcriptId: string, inconsistencies: TranscriptInconsistency[]) => {
    set((state) => ({
      inconsistencies: {
        ...state.inconsistencies,
        [transcriptId]: inconsistencies,
      },
    }));
  },

  addInconsistency: (transcriptId: string, inconsistency: TranscriptInconsistency) => {
    set((state) => {
      const transcriptInconsistencies = state.inconsistencies[transcriptId] || [];

      // Check if inconsistency already exists
      const existingIndex = transcriptInconsistencies.findIndex((i) => i.id === inconsistency.id);
      let updatedInconsistencies: TranscriptInconsistency[];

      if (existingIndex !== -1) {
        // Update existing inconsistency
        updatedInconsistencies = [...transcriptInconsistencies];
        updatedInconsistencies[existingIndex] = inconsistency;
      } else {
        // Add new inconsistency - append to end (inconsistencies are typically shown in order of creation)
        updatedInconsistencies = [...transcriptInconsistencies, inconsistency];
      }

      return {
        inconsistencies: {
          ...state.inconsistencies,
          [transcriptId]: updatedInconsistencies,
        },
      };
    });
  },

  updateInconsistency: (transcriptId: string, inconsistencyId: string, updates: Partial<TranscriptInconsistency>) => {
    set((state) => {
      const transcriptInconsistencies = state.inconsistencies[transcriptId];
      if (!transcriptInconsistencies) {
        return state;
      }

      const updatedInconsistencies = transcriptInconsistencies.map((inconsistency) =>
        inconsistency.id === inconsistencyId ? { ...inconsistency, ...updates } : inconsistency,
      );

      return {
        inconsistencies: {
          ...state.inconsistencies,
          [transcriptId]: updatedInconsistencies,
        },
      };
    });
  },

  updateInconsistencyStatus: (transcriptId: string, inconsistencyId: string, status: TranscriptInconsistencyStatus) => {
    const { updateInconsistency } = get();
    updateInconsistency(transcriptId, inconsistencyId, { status });
  },

  markBlockInconsistenciesAsReprocessing: (transcriptId: string, blockId: string) => {
    set((state) => {
      const transcriptInconsistencies = state.inconsistencies[transcriptId];
      if (!transcriptInconsistencies) {
        return state;
      }

      const updatedInconsistencies = transcriptInconsistencies.map((inconsistency) => {
        // Check if this inconsistency references the edited block
        const referencedBlocks = inconsistency.transcriptSubfact?.referencedBlocks || [];
        const referencesBlock = referencedBlocks.some((block) => block.id === blockId);

        if (referencesBlock && inconsistency.status === TranscriptInconsistencyStatus.Complete) {
          return { ...inconsistency, status: TranscriptInconsistencyStatus.Reprocessing };
        }

        return inconsistency;
      });

      return {
        inconsistencies: {
          ...state.inconsistencies,
          [transcriptId]: updatedInconsistencies,
        },
      };
    });
  },

  // Keep the old method name for backwards compatibility but update to use reprocessing
  markBlockInconsistenciesAsInvalidated: (transcriptId: string, blockId: string) => {
    const { markBlockInconsistenciesAsReprocessing } = get();
    markBlockInconsistenciesAsReprocessing(transcriptId, blockId);
  },

  getInconsistencies: (
    transcriptId: string,
    includeInvalidated: boolean = false,
    includeDismissed: boolean = false,
  ) => {
    const allInconsistencies = get().inconsistencies[transcriptId] || [];

    return allInconsistencies.filter((inconsistency) => {
      const status = inconsistency.status || TranscriptInconsistencyStatus.Complete; // Handle existing data without status

      if (status === TranscriptInconsistencyStatus.Complete || status === TranscriptInconsistencyStatus.Reprocessing) {
        return true;
      }

      if (status === TranscriptInconsistencyStatus.Invalidated) {
        return includeInvalidated;
      }

      if (status === TranscriptInconsistencyStatus.Dismissed) {
        return includeDismissed;
      }

      return false; // Hide unknown statuses by default
    });
  },

  clearInconsistencies: (transcriptId: string) => {
    set((state) => {
      const { [transcriptId]: _, ...remainingInconsistencies } = state.inconsistencies;
      return { inconsistencies: remainingInconsistencies };
    });
  },

  clearAllInconsistencies: () => {
    set({ inconsistencies: {} });
  },

  convertReprocessingToInvalidated: (transcriptId: string, blockId?: string) => {
    set((state) => {
      const transcriptInconsistencies = state.inconsistencies[transcriptId];
      if (!transcriptInconsistencies) {
        return state;
      }

      const updatedInconsistencies = transcriptInconsistencies.map((inconsistency) => {
        // If blockId is specified, only convert inconsistencies that reference that block
        if (blockId) {
          const referencedBlocks = inconsistency.transcriptSubfact?.referencedBlocks || [];
          const referencesBlock = referencedBlocks.some((block) => block.id === blockId);

          if (referencesBlock && inconsistency.status === TranscriptInconsistencyStatus.Reprocessing) {
            return { ...inconsistency, status: TranscriptInconsistencyStatus.Invalidated };
          }
        } else {
          // If no blockId specified, convert all reprocessing inconsistencies
          if (inconsistency.status === TranscriptInconsistencyStatus.Reprocessing) {
            return { ...inconsistency, status: TranscriptInconsistencyStatus.Invalidated };
          }
        }

        return inconsistency;
      });

      return {
        inconsistencies: {
          ...state.inconsistencies,
          [transcriptId]: updatedInconsistencies,
        },
      };
    });
  },
}));
